import React from 'react';

export const GOLD = '#d4af6a';
export const GOLD_SOFT = 'rgba(212, 175, 106, 0.2)';
export const VIOLET = '#9d7bd8';

/** Points of a regular star (or polygon, when inner = outer) centred on 0,0, one tip pointing up. */
export const starPoints = (
    tips: number,
    outer: number,
    inner: number,
    cx = 0,
    cy = 0
): string =>
    Array.from({ length: tips * 2 }, (_, i) => {
        const r = i % 2 === 0 ? outer : inner;
        const a = -Math.PI / 2 + (i * Math.PI) / tips;
        return `${(cx + Math.cos(a) * r).toFixed(1)},${(cy + Math.sin(a) * r).toFixed(1)}`;
    }).join(' ');

/** A pentagram drawn as one line through every second vertex of a pentagon. */
const pentagram = (r: number, flip = 1): string =>
    [0, 2, 4, 1, 3, 0]
        .map((k) => {
            const a = -Math.PI / 2 + (k * 2 * Math.PI) / 5;
            return `${(Math.cos(a) * r).toFixed(1)},${(flip * Math.sin(a) * r).toFixed(1)}`;
        })
        .join(' ');

const around = (n: number, r: number, cx = 0, cy = 0) =>
    Array.from({ length: n }, (_, i) => {
        const a = (i * 2 * Math.PI) / n - Math.PI / 2;
        return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r, a };
    });

const Rays: React.FC<{ n: number; from: number; to: number; alt?: number }> = ({
    n,
    from,
    to,
    alt = 0,
}) => (
    <>
        {around(n, 1).map((p, i) => {
            const end = i % 2 === 0 ? to : to - alt;
            return (
                <line
                    key={i}
                    x1={p.x * from}
                    y1={p.y * from}
                    x2={p.x * end}
                    y2={p.y * end}
                />
            );
        })}
    </>
);

const INFINITY =
    'M0 0 C 10 -14 30 -14 30 0 C 30 14 10 14 0 0 C -10 -14 -30 -14 -30 0 C -30 14 -10 14 0 0';

/** Suit symbols, about 48 units tall, centred on 0,0. */
export const SuitGlyph: React.FC<{ suit: string }> = ({ suit }) => {
    switch (suit) {
        case 'wands':
            return (
                <g>
                    <path d="M0 -26 V26" />
                    <path
                        d="M0 -12 q-9 -2 -11 -11 q9 0 11 11Z M0 2 q9 -2 11 -11 q-9 0 -11 11Z M0 16 q-9 -2 -11 -11 q9 0 11 11Z"
                        fill={GOLD_SOFT}
                    />
                    <circle cx="0" cy="-27" r="2.5" fill={GOLD} />
                </g>
            );
        case 'cups':
            return (
                <g>
                    <path
                        d="M-14 -14 H14 C14 6 7 11 0 11 C-7 11 -14 6 -14 -14Z"
                        fill={GOLD_SOFT}
                    />
                    <path d="M0 11 V21 M-9 24 H9 M-6 21 H6" />
                    <path d="M-14 -14 q14 7 28 0" />
                </g>
            );
        case 'swords':
            return (
                <g>
                    <path d="M0 -28 L5 -6 V10 H-5 V-6Z" fill={GOLD_SOFT} />
                    <path d="M-13 10 H13 M0 10 V22" />
                    <circle cx="0" cy="25" r="2.6" fill={GOLD} />
                </g>
            );
        default:
            return (
                <g>
                    <circle r="19" fill={GOLD_SOFT} />
                    <circle r="15" />
                    <polyline points={pentagram(14)} />
                </g>
            );
    }
};

/** The 22 major arcana as small sigils, about 100 units across, centred on 0,0. */
export const MajorSigil: React.FC<{ rank: number }> = ({ rank }) => {
    switch (rank) {
        case 0: // Fool: sun over a cliff edge, a step into the air
            return (
                <g>
                    <circle cx="0" cy="-30" r="10" fill={GOLD_SOFT} />
                    <g transform="translate(0 -30)">
                        <Rays n={12} from={14} to={21} />
                    </g>
                    <path d="M-46 46 H-6 L-2 22" />
                    <circle cx="6" cy="8" r="5" />
                    <path d="M6 13 V28 M6 18 L-4 24 M6 18 L16 14" />
                    <path d="M14 34 q8 4 14 12" strokeDasharray="3 4" />
                </g>
            );
        case 1: // Magician: infinity, wand, the four suits on the table
            return (
                <g>
                    <g transform="translate(0 -32) scale(0.8)">
                        <path d={INFINITY} />
                    </g>
                    <path d="M0 -14 V30" />
                    <circle cx="0" cy="-16" r="3" fill={GOLD} />
                    <path d="M-40 38 H40" />
                    <circle cx="-27" cy="30" r="5" />
                    <rect
                        x="-13"
                        y="25"
                        width="9"
                        height="9"
                        transform="rotate(45 -8.5 29.5)"
                    />
                    <path d="M9 34 L14 25 L19 34Z" />
                    <path d="M27 24 V35 M22 29.500 H32" />
                </g>
            );
        case 2: // High Priestess: two pillars and the moon
            return (
                <g>
                    <rect
                        x="-40"
                        y="-44"
                        width="12"
                        height="88"
                        fill={GOLD_SOFT}
                    />
                    <rect
                        x="28"
                        y="-44"
                        width="12"
                        height="88"
                        fill={GOLD_SOFT}
                    />
                    <path
                        d="M8 -24 A22 22 0 1 0 8 24 A17 17 0 1 1 8 -24Z"
                        fill={GOLD_SOFT}
                    />
                    <circle cx="0" cy="34" r="4" />
                    <path d="M-14 -34 H14" />
                </g>
            );
        case 3: // Empress: Venus within a ring of stars
            return (
                <g>
                    <circle cx="0" cy="-8" r="15" fill={GOLD_SOFT} />
                    <path d="M0 7 V38 M-12 24 H12" />
                    {around(12, 44).map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r="2.4" fill={GOLD} />
                    ))}
                </g>
            );
        case 4: // Emperor: stone throne and ram's horns
            return (
                <g>
                    <rect
                        x="-24"
                        y="-18"
                        width="48"
                        height="62"
                        fill={GOLD_SOFT}
                    />
                    <rect x="-13" y="-6" width="26" height="30" />
                    <path d="M-24 -18 C-46 -22 -44 6 -30 4 M24 -18 C46 -22 44 6 30 4" />
                    <path d="M-30 -30 H30" />
                    <circle cx="0" cy="-34" r="4" fill={GOLD} />
                </g>
            );
        case 5: // Hierophant: triple cross and two keys
            return (
                <g>
                    <path d="M0 -44 V44 M-10 -30 H10 M-17 -14 H17 M-24 2 H24" />
                    <circle cx="-38" cy="30" r="5" />
                    <path d="M-38 35 V46 M-38 41 H-33" />
                    <circle cx="38" cy="30" r="5" />
                    <path d="M38 35 V46 M38 41 H33" />
                </g>
            );
        case 6: // Lovers: two circles that overlap, a star above
            return (
                <g>
                    <circle cx="-15" cy="12" r="24" fill={GOLD_SOFT} />
                    <circle cx="15" cy="12" r="24" fill={GOLD_SOFT} />
                    <polygon
                        points={starPoints(8, 14, 5, 0, -32)}
                        fill={GOLD_SOFT}
                    />
                </g>
            );
        case 7: // Chariot: canopy of stars, a cart on two wheels
            return (
                <g>
                    <path d="M-34 -8 Q0 -44 34 -8" />
                    {[-18, 0, 18].map((x) => (
                        <circle
                            key={x}
                            cx={x}
                            cy={-22 + Math.abs(x) * 0.45}
                            r="2.5"
                            fill={GOLD}
                        />
                    ))}
                    <rect
                        x="-30"
                        y="-4"
                        width="60"
                        height="30"
                        fill={GOLD_SOFT}
                    />
                    <circle cx="-20" cy="34" r="10" />
                    <circle cx="20" cy="34" r="10" />
                    <path
                        d="M-20 24 V44 M-30 34 H-10 M20 24 V44 M10 34 H30"
                        strokeWidth="1.2"
                    />
                </g>
            );
        case 8: // Strength: infinity over a lion's mane
            return (
                <g>
                    <g transform="translate(0 -38) scale(0.55)">
                        <path d={INFINITY} />
                    </g>
                    <polygon
                        points={starPoints(14, 32, 22, 0, 12)}
                        fill={GOLD_SOFT}
                    />
                    <circle cx="0" cy="12" r="13" />
                    <path d="M-5 9 h2 M3 9 h2 M-4 17 q4 3 8 0" />
                </g>
            );
        case 9: // Hermit: a lantern with a star, on a mountain
            return (
                <g>
                    <circle cx="0" cy="-20" r="21" />
                    <polygon
                        points={starPoints(6, 15, 8.7, 0, -20)}
                        fill={GOLD_SOFT}
                    />
                    <path d="M0 2 V46" />
                    <path d="M-46 46 L-24 16 L-8 46 M-8 46 L14 26 L34 46" />
                </g>
            );
        case 10: // Wheel of Fortune
            return (
                <g>
                    <circle r="34" />
                    <circle r="24" fill={GOLD_SOFT} />
                    <circle r="6" fill={GOLD} />
                    <Rays n={8} from={6} to={34} />
                    {around(8, 43).map((p, i) => (
                        <circle key={i} cx={p.x} cy={p.y} r="2.6" fill={GOLD} />
                    ))}
                </g>
            );
        case 11: // Justice: scales and a sword
            return (
                <g>
                    <path d="M0 -40 V32 M-34 -26 H34" />
                    <path
                        d="M-34 -26 L-44 0 M-34 -26 L-24 0 M-46 0 H-22 M-46 0 Q-34 14 -22 0"
                        fill={GOLD_SOFT}
                    />
                    <path
                        d="M34 -26 L24 0 M34 -26 L44 0 M22 0 H46 M22 0 Q34 14 46 0"
                        fill={GOLD_SOFT}
                    />
                    <path d="M-10 32 H10 M0 32 V46" />
                </g>
            );
        case 12: // Hanged Man: a halo around the head that points down
            return (
                <g>
                    <path d="M-36 -44 H36 M-36 -44 V-30 M36 -44 V-30" />
                    <path d="M0 -44 V-18" />
                    <path d="M-13 -18 H13 L0 14Z" fill={GOLD_SOFT} />
                    <circle cx="0" cy="24" r="8" />
                    <circle cx="0" cy="24" r="14" strokeDasharray="2 3" />
                </g>
            );
        case 13: // Death: a rose, and the sun rising between two towers
            return (
                <g>
                    {around(5, 14, 0, -10).map((p, i) => (
                        <circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r="10"
                            fill={GOLD_SOFT}
                        />
                    ))}
                    <circle cx="0" cy="-10" r="5" fill={GOLD} />
                    <path d="M-44 46 H44" />
                    <path d="M-30 46 A30 30 0 0 1 30 46" />
                    <g transform="translate(0 46)">
                        <Rays n={9} from={34} to={44} />
                    </g>
                </g>
            );
        case 14: // Temperance: two cups, one stream
            return (
                <g>
                    <path d="M-40 -14 H-16 L-20 12 H-36Z" fill={GOLD_SOFT} />
                    <path d="M16 6 H40 L36 32 H20Z" fill={GOLD_SOFT} />
                    <path d="M-18 -8 C 0 -6 2 6 18 12" strokeDasharray="4 3" />
                    <circle cx="0" cy="-38" r="9" />
                    <g transform="translate(0 -38)">
                        <Rays n={8} from={12} to={18} />
                    </g>
                </g>
            );
        case 15: // Devil: inverted pentagram in a circle, horns
            return (
                <g>
                    <circle r="30" />
                    <polyline points={pentagram(25, -1)} fill={GOLD_SOFT} />
                    <path d="M-16 -32 Q-30 -52 -10 -52 M16 -32 Q30 -52 10 -52" />
                </g>
            );
        case 16: // Tower: a crowned tower struck by lightning
            return (
                <g>
                    <rect
                        x="-14"
                        y="-4"
                        width="28"
                        height="48"
                        fill={GOLD_SOFT}
                    />
                    <polygon
                        points="-18,-4 -14,-24 -6,-12 0,-28 6,-12 14,-24 18,-4"
                        fill={GOLD_SOFT}
                    />
                    <path d="M-2 14 v8 M-2 30 v8" />
                    <path
                        d="M34 -46 L20 -20 L30 -20 L14 8"
                        fill="none"
                        strokeWidth="2.8"
                    />
                    <circle cx="-32" cy="2" r="2.5" fill={GOLD} />
                    <circle cx="-38" cy="22" r="2" fill={GOLD} />
                    <circle cx="36" cy="26" r="2.5" fill={GOLD} />
                </g>
            );
        case 17: // Star: one great star, seven small
            return (
                <g>
                    <polygon
                        points={starPoints(8, 28, 9, 0, -4)}
                        fill={GOLD_SOFT}
                    />
                    {around(7, 42, 0, -4).map((p, i) => (
                        <polygon
                            key={i}
                            points={starPoints(5, 4.2, 1.8, p.x, p.y)}
                            fill={GOLD}
                        />
                    ))}
                    <path d="M-10 40 q10 8 20 0" />
                </g>
            );
        case 18: // Moon: crescent in a circle, towers, falling drops
            return (
                <g>
                    <circle cx="0" cy="-12" r="34" strokeDasharray="1 4" />
                    <path
                        d="M10 -36 A24 24 0 1 0 10 12 A18 18 0 1 1 10 -36Z"
                        fill={GOLD_SOFT}
                    />
                    <rect x="-44" y="22" width="14" height="24" />
                    <rect x="30" y="22" width="14" height="24" />
                    <path d="M-12 30 v6 M0 34 v8 M12 30 v6" />
                </g>
            );
        case 19: // Sun
            return (
                <g>
                    <circle r="19" fill={GOLD_SOFT} />
                    <circle r="12" />
                    <Rays n={16} from={25} to={45} alt={12} />
                </g>
            );
        case 20: // Judgement: a trumpet and three rising figures
            return (
                <g>
                    <path d="M-40 -18 L8 -40 L8 -6Z" fill={GOLD_SOFT} />
                    <path d="M8 -36 L28 -44 M8 -23 L32 -23 M8 -10 L28 -2" />
                    {[-26, 0, 26].map((x, i) => (
                        <g
                            key={x}
                            transform={`translate(${x} ${34 - (i === 1 ? 4 : 0)})`}
                        >
                            <circle cx="0" cy="-6" r="5" />
                            <path d="M-8 12 Q0 -2 8 12" />
                        </g>
                    ))}
                </g>
            );
        default: // 21 World: a wreath around the dancer's diamond
            return (
                <g>
                    <ellipse rx="30" ry="44" strokeDasharray="1 4" />
                    {Array.from({ length: 16 }, (_, i) => {
                        const a = (i * 2 * Math.PI) / 16;
                        return (
                            <ellipse
                                key={i}
                                cx={Math.cos(a) * 30}
                                cy={Math.sin(a) * 44}
                                rx="6"
                                ry="3"
                                transform={`rotate(${(a * 180) / Math.PI + 90} ${Math.cos(a) * 30} ${Math.sin(a) * 44})`}
                                fill={GOLD_SOFT}
                            />
                        );
                    })}
                    <path d="M0 -20 L12 0 L0 20 L-12 0Z" fill={GOLD_SOFT} />
                    {[
                        [-42, -46],
                        [42, -46],
                        [-42, 46],
                        [42, 46],
                    ].map(([x, y]) => (
                        <circle
                            key={`${x}${y}`}
                            cx={x}
                            cy={y}
                            r="3"
                            fill={GOLD}
                        />
                    ))}
                </g>
            );
    }
};
